import React, { useState, useEffect, useCallback } from 'react';
import RichTextEditor from '../../components/RichTextEditor';
import ModalPortal from '../../components/Modal/ModalPortal';
import { uploadFile } from '../../utils/fileUpload';

const DEFAULT_FORM_DATA = {
  title: '',
  destination: '',
  duration_days: '',
  location_description: '',
  short_description: '',
  highlights_rich: '',
  activities: '',
  hero_image_url: '',
  pdf_url: '',
  external_url: ''
};

const UploadItineraryModal = ({ isOpen, onClose, email, onSuccess, editItinerary = null }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageUploadType, setImageUploadType] = useState('url'); // 'url' or 'file'
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const isEditMode = Boolean(editItinerary?.id);

  const resetForm = useCallback(() => {
    setFormData({ ...DEFAULT_FORM_DATA });
    setImageFile(null);
    setImagePreview('');
    setImageUploadType('url');
    setPdfFile(null);
    setPdfFileName('');
    setError('');
    setUploadProgress('');
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    if (!isEditMode) {
      resetForm();
      return;
    }

    const parsedDurationDays =
      editItinerary?.duration_days ??
      (() => {
        const match = String(editItinerary?.duration || '').match(/^(\d+)/);
        return match ? Number(match[1]) : '';
      })();

    setFormData({
      title: editItinerary?.title || '',
      destination: editItinerary?.destination || '',
      duration_days: parsedDurationDays === '' ? '' : String(parsedDurationDays),
      location_description: editItinerary?.location_description || '',
      short_description: editItinerary?.short_description || editItinerary?.description || '',
      highlights_rich: editItinerary?.highlights_rich || '',
      activities: Array.isArray(editItinerary?.activities)
        ? editItinerary.activities.join(', ')
        : (editItinerary?.activities || ''),
      hero_image_url: editItinerary?.hero_image_url || '',
      pdf_url: editItinerary?.pdf_url || '',
      external_url: editItinerary?.external_url || ''
    });
    setImageFile(null);
    setImagePreview(editItinerary?.hero_image_url || '');
    setImageUploadType('url');
    setPdfFile(null);
    setPdfFileName('');
    setError('');
    setUploadProgress('');
  }, [isOpen, isEditMode, editItinerary, resetForm]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Update preview for URL input
    if (field === 'hero_image_url' && imageUploadType === 'url') {
      setImagePreview(value);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  // Handle PDF file selection
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        setError('PDF file size must be less than 50MB');
        return;
      }

      setPdfFile(file);
      setPdfFileName(file.name);
      setError('');
    }
  };

  // Upload image to server
  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      const url = await uploadFile(imageFile, {
        onProgress: (status) => setUploadProgress(status === 'Converting image format...' ? status : 'Uploading image...')
      });
      setUploadProgress('');
      return url;
    } catch (err) {
      setUploadProgress('');
      throw new Error('Failed to upload image: ' + err.message);
    }
  };

  // Upload PDF to server
  const uploadPdf = async () => {
    if (!pdfFile) return null;

    try {
      const url = await uploadFile(pdfFile, {
        onProgress: () => setUploadProgress('Uploading PDF...')
      });
      setUploadProgress('');
      return url;
    } catch (err) {
      setUploadProgress('');
      throw new Error('Failed to upload PDF: ' + err.message);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.short_description.trim()) {
        throw new Error('Short description is required');
      }

      let finalImageUrl = formData.hero_image_url;
      let finalPdfUrl = formData.pdf_url;
      const finalExternalUrl = formData.external_url?.trim() || null;

      // Upload image if file is selected
      if (imageUploadType === 'file' && imageFile) {
        finalImageUrl = await uploadImage();
      }

      // Upload PDF if file is selected
      if (pdfFile) {
        finalPdfUrl = await uploadPdf();
      }

      // Parse activities into array
      const activitiesArray = formData.activities
        ? formData.activities.split(',').map(a => a.trim()).filter(a => a)
        : [];

      // Prepare submission data
      const submitData = {
        email,
        title: formData.title.trim(),
        destination: formData.destination.trim() || null,
        duration_days: formData.duration_days ? parseInt(formData.duration_days) : null,
        location_description: formData.location_description.trim() || null,
        short_description: formData.short_description.trim(),
        highlights_rich: formData.highlights_rich || null,
        activities: activitiesArray,
        hero_image_url: finalImageUrl || null,
        pdf_url: finalPdfUrl || null,
        external_url: finalExternalUrl
      };
      if (isEditMode) {
        submitData.itinerary_id = editItinerary.id;
        submitData.itinerary_type = editItinerary.type || null;
      }

      setUploadProgress(isEditMode ? 'Saving changes...' : 'Saving itinerary...');

      // Submit to API
      const response = await fetch('/api/itinerary/create', {
        method: isEditMode ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (result.ok) {
        setUploadProgress('');
        if (onSuccess) onSuccess(result);
        onClose();
      } else {
        throw new Error(result.message || result.error || 'Failed to save itinerary');
      }
    } catch (err) {
      setError(err.message);
      setUploadProgress('');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%',
    border: 'none',
    borderBottom: '2px solid rgba(0,0,0,.12)',
    background: 'transparent',
    padding: '8px 0',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
    fontSize: 16,
    color: '#0f084b',
    outline: 'none'
  };

  const labelStyle = {
    fontFamily: "'Sailors', serif",
    fontSize: 14,
    color: '#0f084b',
    marginBottom: 4,
    display: 'block'
  };

  const textAreaStyle = {
    ...inputStyle,
    border: '2px solid rgba(0,0,0,.12)',
    borderRadius: 8,
    padding: 12,
    minHeight: 80,
    resize: 'vertical'
  };

  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="800px">
        <div style={{ padding: 32 }}>
          <h2 style={{
            fontFamily: "'Sailors', serif",
            fontSize: 28,
            color: '#0f084b',
            marginBottom: 24,
            textAlign: 'center'
          }}>
            {isEditMode ? 'Edit Itinerary' : 'Upload Itinerary'}
          </h2>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: 24 }}>
              {/* Title */}
              <div>
                <label style={labelStyle}>
                  Title <span style={{ color: '#dc1d08' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  style={inputStyle}
                  placeholder="e.g., Tropical Paradise Getaway"
                  required
                />
              </div>

              {/* Destination */}
              <div>
                <label style={labelStyle}>Destination</label>
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => handleChange('destination', e.target.value)}
                  style={inputStyle}
                  placeholder="e.g., Bali, Indonesia"
                />
              </div>

              {/* Duration */}
              <div>
                <label style={labelStyle}>Duration (days)</label>
                <input
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => handleChange('duration_days', e.target.value)}
                  style={inputStyle}
                  placeholder="e.g., 7"
                  min="1"
                />
              </div>

              {/* Location Description */}
              <div>
                <label style={labelStyle}>Location Description</label>
                <textarea
                  value={formData.location_description}
                  onChange={(e) => handleChange('location_description', e.target.value)}
                  style={textAreaStyle}
                  placeholder="Describe the location and what makes it special..."
                />
              </div>

              {/* Short Description */}
              <div>
                <label style={labelStyle}>
                  Short Description <span style={{ color: '#dc1d08' }}>*</span>
                </label>
                <textarea
                  value={formData.short_description}
                  onChange={(e) => handleChange('short_description', e.target.value)}
                  style={textAreaStyle}
                  placeholder="Brief overview of the itinerary that will display on the itinerary page..."
                  required
                />
              </div>

              {/* External Itinerary URL */}
              <div>
                <label style={labelStyle}>Itinerary URL or Instagram Post/Reel URL</label>
                <input
                  type="url"
                  value={formData.external_url}
                  onChange={(e) => handleChange('external_url', e.target.value)}
                  style={inputStyle}
                  placeholder="https://yourblog.com/your-itinerary"
                />
                <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12, color: '#666', marginTop: 4 }}>
                  Optional — link to an itinerary on your site or elsewhere.
                </div>
              </div>

              {/* PDF Upload */}
              <div>
                <label style={labelStyle}>
                  Upload Itinerary PDF
                  <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
                    (Optional - Max 50MB)
                  </span>
                </label>
                <div style={{ 
                  border: '2px dashed rgba(0,0,0,.12)', 
                  borderRadius: 8, 
                  padding: 16,
                  textAlign: 'center',
                  background: pdfFileName ? 'rgba(220, 29, 8, 0.05)' : 'transparent'
                }}>
                  {pdfFileName ? (
                    <div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: 8,
                        marginBottom: 12
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 12H14" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 16H14" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span style={{ 
                          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                          fontSize: 14,
                          color: '#0f084b'
                        }}>
                          {pdfFileName}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setPdfFile(null);
                          setPdfFileName('');
                        }}
                        style={{
                          background: '#dc1d08',
                          color: 'white',
                          border: 'none',
                          borderRadius: 100,
                          padding: '8px 16px',
                          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                          fontSize: 14,
                          cursor: 'pointer'
                        }}
                      >
                        Remove PDF
                      </button>
                    </div>
                  ) : formData.pdf_url ? (
                    <div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: 8,
                        marginBottom: 12
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#0f084b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#0f084b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 12H14" stroke="#0f084b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 16H14" stroke="#0f084b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <a
                          href={formData.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                            fontSize: 14,
                            color: '#0f084b',
                            textDecoration: 'underline'
                          }}
                        >
                          View current PDF
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, pdf_url: '' }))}
                        style={{
                          background: 'transparent',
                          color: '#dc1d08',
                          border: '2px solid #dc1d08',
                          borderRadius: 100,
                          padding: '8px 16px',
                          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                          fontSize: 14,
                          cursor: 'pointer'
                        }}
                      >
                        Remove Current PDF
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={handlePdfChange}
                        style={{ display: 'none' }}
                        id="pdf-upload"
                      />
                      <label 
                        htmlFor="pdf-upload"
                        style={{
                          display: 'inline-block',
                          background: '#0f084b',
                          color: 'white',
                          border: 'none',
                          borderRadius: 100,
                          padding: '10px 24px',
                          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                          fontSize: 14,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(15, 8, 75, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        Choose PDF File
                      </label>
                      <p style={{ 
                        marginTop: 8,
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        fontSize: 12,
                        color: '#666',
                        margin: '8px 0 0 0'
                      }}>
                        Upload an existing itinerary PDF for easy access
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Highlights (Rich Text) */}
              <div>
                <label style={labelStyle}>Write your itinerary</label>
                <RichTextEditor
                  value={formData.highlights_rich}
                  onChange={(value) => handleChange('highlights_rich', value)}
                  placeholder="Add highlights of your itinerary..."
                />
              </div>

              {/* Activities */}
              <div>
                <label style={labelStyle}>
                  Activities
                  <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
                    (comma-separated)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.activities}
                  onChange={(e) => handleChange('activities', e.target.value)}
                  style={inputStyle}
                  placeholder="e.g., Photography, Culture, Adventure, Beach"
                />
              </div>

              {/* Hero Image */}
              <div>
                <label style={labelStyle}>Hero Image</label>
                
                {/* Toggle between URL and File upload */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="url"
                      checked={imageUploadType === 'url'}
                      onChange={() => {
                        setImageUploadType('url');
                        setImageFile(null);
                        setImagePreview(formData.hero_image_url);
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <span style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14 }}>
                      Image URL
                    </span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="file"
                      checked={imageUploadType === 'file'}
                      onChange={() => {
                        setImageUploadType('file');
                        setFormData(prev => ({ ...prev, hero_image_url: '' }));
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <span style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14 }}>
                      Upload File
                    </span>
                  </label>
                </div>

                {/* URL Input */}
                {imageUploadType === 'url' && (
                  <input
                    type="url"
                    value={formData.hero_image_url}
                    onChange={(e) => handleChange('hero_image_url', e.target.value)}
                    style={inputStyle}
                    placeholder="https://example.com/image.jpg"
                  />
                )}

                {/* File Upload */}
                {imageUploadType === 'file' && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        padding: '12px 24px',
                        backgroundColor: '#f9f9f9',
                        border: '2px dashed #0f084b',
                        borderRadius: 8,
                        cursor: 'pointer',
                        textAlign: 'center',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        fontSize: 14,
                        color: '#0f084b',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                        e.currentTarget.style.borderColor = '#dc1d08';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9f9f9';
                        e.currentTarget.style.borderColor = '#0f084b';
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      {imageFile ? imageFile.name : 'Choose Image File'}
                    </label>
                  </div>
                )}

                {/* Image Preview */}
                {imagePreview && (
                  <div style={{ marginTop: 16 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        borderRadius: 8,
                        objectFit: 'cover'
                      }}
                      onError={() => setImagePreview('')}
                    />
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  padding: 16,
                  backgroundColor: '#fff5f5',
                  border: '2px solid #dc1d08',
                  color: '#dc1d08',
                  borderRadius: 8,
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12
                }}>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ flexShrink: 0, marginTop: 2 }}
                  >
                    <path 
                      d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                      stroke="#dc1d08" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div style={{ flex: 1, lineHeight: 1.5 }}>
                    {error}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploadProgress && (
                <div style={{
                  padding: 12,
                  backgroundColor: '#e8f4fd',
                  color: '#0f084b',
                  borderRadius: 8,
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: 14,
                  textAlign: 'center'
                }}>
                  {uploadProgress}
                </div>
              )}

              {/* Submit Button */}
              <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#0f084b',
                    border: '2px solid #0f084b',
                    borderRadius: 8,
                    padding: '12px 32px',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f084b';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#0f084b';
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    backgroundColor: '#dc1d08',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 32px',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.backgroundColor = '#b51706';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 29, 8, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc1d08';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? (isEditMode ? 'Saving...' : 'Uploading...')
                    : (isEditMode ? 'Save Changes' : 'Upload Itinerary')}
                </button>
              </div>
            </div>
          </form>
        </div>
    </ModalPortal>
  );
};

export default UploadItineraryModal;
